import { ChangeEvent } from 'react';
import { addDays } from 'date-fns';
import { UseFormMethods } from 'react-hook-form';
import { getStartOfToday } from 'modules/core/utils';
import { SetFieldValue } from 'modules/core/types/form';
import { IBalloonVehicle, IResidualValues } from 'modules/core/types/balloon';
import { IOption, IRequestFlags } from 'modules/core/types';
import { IDealSourceData } from 'modules/core/types/dealSource';
import { ADDENDUM_STEP_DELAY, STEP_ANIMATION_DURATION_MS } from 'modules/core/constants';
import { vehicleApi } from 'api';
import { DealSourceProvider, RequestStatus } from 'modules/core/enums';
import { ReportService } from 'modules/core/services';
import { AddendumStatus } from 'modules/core/enums/addendumStatus.enum';

import { LOAN_VARIABLES_FIELDS_NAMES } from '../consts/fieldNames.const';
import { DEFAULT_DAYS_TO_FIRST_PAYMENT, MAX_DAYS_TO_FIRST_PAYMENT, MIN_DAYS_TO_FIRST_PAYMENT } from '../consts/consts';
import { DEFAULT_RESIDUAL_VALUE } from './BalloonPayment.const';

export const getAnnualMileages = (residualValues: IResidualValues | null): number[] => {
  if (!residualValues?.perAnnualMileage) return [];

  return Object.keys(residualValues.perAnnualMileage).map(Number);
};

export const getBalloonTerms = (residualValues: IResidualValues | null): number[] => {
  if (!residualValues?.perAnnualMileage) return [];

  const firstMileageEntry = Object.values(residualValues.perAnnualMileage)[0];

  return firstMileageEntry ? Object.keys(firstMileageEntry.perTerm).map(Number) : [];
};

export const getResidualRequestParams = (
  balloonVehicle: IBalloonVehicle | null,
  odometer: number,
  engine: IOption,
): Record<string, any> | null => {
  if (!balloonVehicle) return null;

  return {
    model_year: balloonVehicle.modelYear,
    make: balloonVehicle.make,
    model: balloonVehicle.model,
    style: balloonVehicle.style,
    engine: engine.value || balloonVehicle.engine,
    odometer: odometer,
  };
};

export const getBalloonPayment = (recalculateData: any, annualMileage: number, loanTerm: number): number => {
  const { balloonResidualValues = {} } = recalculateData;

  return balloonResidualValues?.perAnnualMileage?.[annualMileage]?.perTerm?.[loanTerm] || DEFAULT_RESIDUAL_VALUE;
};

export interface IFirstPaymentDateInfo {
  date: Date;
  defaultDays: number;
  minDays: number;
  maxDays: number;
}

export const getFirstPaymentDate = async (
  afgDealerId: string,
  afgLenderId: string,
  contractDate?: Date | null,
): Promise<IFirstPaymentDateInfo> => {
  const baseDate = contractDate ?? getStartOfToday();

  try {
    const { data } = await vehicleApi.getBalloonDaysToFirstPayment({
      dealer_id: afgDealerId,
      lender_id: afgLenderId,
    });
    const defaultDays = Number.isFinite(data?.default) ? data.default : DEFAULT_DAYS_TO_FIRST_PAYMENT;
    const minDays = Number.isFinite(data?.min) ? data.min : MIN_DAYS_TO_FIRST_PAYMENT;
    const maxDays = Number.isFinite(data?.max) ? data.max : MAX_DAYS_TO_FIRST_PAYMENT;

    return { date: addDays(baseDate, defaultDays), defaultDays, minDays, maxDays };
  } catch {
    return {
      date: addDays(baseDate, DEFAULT_DAYS_TO_FIRST_PAYMENT),
      defaultDays: DEFAULT_DAYS_TO_FIRST_PAYMENT,
      minDays: MIN_DAYS_TO_FIRST_PAYMENT,
      maxDays: MAX_DAYS_TO_FIRST_PAYMENT,
    };
  }
};

export const balloonToggleCallback = async (
  event: ChangeEvent<HTMLInputElement>,
  formMethods: UseFormMethods,
  setHistoryCallback: Function,
  startRecalculateAnimations: Function,
  balloonVehicleInfoFlags: IRequestFlags,
  cancelDebouncedRecalculate: Function,
  handleChangeCallback: Function,
  loanTermPerTab: any,
  setLoanTermPerTab: Function,
  loanTerm: IOption,
) => {
  const isChecked = event.target.checked;
  const { setValue, trigger, unregister } = formMethods;

  setValue(LOAN_VARIABLES_FIELDS_NAMES.BALLOON_TOGGLE, isChecked);
  setHistoryCallback({ loanTerm: isChecked ? loanTermPerTab.balloon : loanTermPerTab.retail });
  cancelDebouncedRecalculate();

  if (!isChecked) {
    unregister(LOAN_VARIABLES_FIELDS_NAMES.ANNUAL_MILEAGE);
    unregister(LOAN_VARIABLES_FIELDS_NAMES.AFG_TRIM);
    unregister(LOAN_VARIABLES_FIELDS_NAMES.CONTRACT_DATE);
    unregister(LOAN_VARIABLES_FIELDS_NAMES.FIRST_PAYMENT_DATE);
    unregister(LOAN_VARIABLES_FIELDS_NAMES.CREATE_ADDENDUM);
    unregister(LOAN_VARIABLES_FIELDS_NAMES.AFG_SUBMIT_DEAL);
  } else if (balloonVehicleInfoFlags.isFetching) {
    startRecalculateAnimations(false);
  }

  setLoanTermPerTab({
    ...loanTermPerTab,
    ...(isChecked ? { retail: loanTerm } : { balloon: loanTerm }),
  });

  await trigger();

  if (!isChecked || balloonVehicleInfoFlags.isLoaded) {
    handleChangeCallback({ target: { name: LOAN_VARIABLES_FIELDS_NAMES.BALLOON_TOGGLE } });
  }
};

export const matchLoanTerms = (loanTerms: number[], balloonTerms: number[] | undefined) => {
  if (!balloonTerms) return [];

  return loanTerms.filter((term) => balloonTerms.includes(term));
};

export const updateBalloonPaymentFormValues = (
  selectedBalloonVehicle: IBalloonVehicle | null,
  balloonVehicleInfo: IBalloonVehicle[],
  setValue: SetFieldValue,
  overrideFormHistoryValues: Function,
) => {
  const historyData: any = {};

  if (selectedBalloonVehicle) {
    const afgTrim = selectedBalloonVehicle.style;
    const afgTrimValue = {
      title: afgTrim,
      value: balloonVehicleInfo.findIndex((info) => info.style === afgTrim),
    };

    setValue(LOAN_VARIABLES_FIELDS_NAMES.AFG_TRIM, afgTrimValue);
    historyData.afgTrim = afgTrimValue;
  }

  if (selectedBalloonVehicle?.engine) {
    const engineValue = {
      title: selectedBalloonVehicle.engine,
      value: selectedBalloonVehicle.engine,
    };

    setValue(LOAN_VARIABLES_FIELDS_NAMES.ENGINE, engineValue);
    historyData.engine = engineValue;
  }

  overrideFormHistoryValues(historyData);
};

export const handleAddendumFlow = async (
  afgLenderId: string,
  afgDealerId: string,
  dealSourceData: IDealSourceData,
  selectedBalloonVehicle: IBalloonVehicle,
  quoteIdRef: any,
  loanVariables: any,
  getValues: Function,
  sendRouteOneData: Function,
  addendumStatus: AddendumStatus,
  addendumStep: number,
  setAddendumStatus: Function,
  setAddendumStep: Function,
) => {
  const createQuote = async () => {
    const {
      loanTerm,
      balloonPayment,
      mileage,
      engine,
      annualMileage,
      contractDate,
      firstPaymentDate,
    } = getValues().loanVariablesForm;

    const { data } = await vehicleApi.createBalloonQuote(
      { deal_source: DealSourceProvider.ROUTE_ONE },
      {
        lender_id: afgLenderId,
        dealer_id: afgDealerId,
        vin: loanVariables.vin,
        engine: engine?.value,
        model_year: selectedBalloonVehicle?.modelYear,
        make: selectedBalloonVehicle?.make,
        model: selectedBalloonVehicle?.model,
        style: selectedBalloonVehicle?.style,
        odometer: mileage,
        total_amount_financed: loanVariables.total_financed,
        term: loanTerm?.value,
        annual_mileage: annualMileage?.value,
        residual: balloonPayment,
        contract_date: contractDate.toLocaleDateString('en-CA'),
        first_payment_date: firstPaymentDate.toLocaleDateString('en-CA'),
        conversation_id: dealSourceData?.conversation_id,
        deal_dealer_id: dealSourceData?.dealer_id,
        deal_jacket_id: dealSourceData?.deal_jacket_id,
      },
    );

    quoteIdRef.current = data;
  };

  const downloadAddendum = async () => {
    const { data, headers } = await vehicleApi.downloadBalloonAddendum({
      lender_id: afgLenderId,
      dealer_id: afgDealerId,
      quote_id: quoteIdRef.current,
    });

    const contentDisposition = headers['content-disposition'] ?? '';
    const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\r\n]+)["']?/i);
    const fileName = fileNameMatch ? decodeURIComponent(fileNameMatch[1].trim()) : 'Balloon Payment Addendum.pdf';

    ReportService.downloadReport(data, fileName);
  };

  const submitDealData = async () => {
    const { afgSubmitDeal } = getValues().loanVariablesForm;

    if (!afgSubmitDeal) return;

    const response = await sendRouteOneData(ADDENDUM_STEP_DELAY + STEP_ANIMATION_DURATION_MS);

    if (response.meta.requestStatus !== RequestStatus.FULFILLED) throw new Error();

    return response.payload?.conversationId ?? null;
  };

  const stepDelay = () =>
    new Promise<void>((resolve) => {
      setTimeout(resolve, ADDENDUM_STEP_DELAY);
    });

  const runStep = async <T>(step: number, action: () => Promise<T>): Promise<T | undefined> => {
    if (currentStep > step) return;

    const result = await action();

    await stepDelay();
    setAddendumStep(step + 1);

    return result;
  };

  const currentStep = addendumStatus === AddendumStatus.FAILED && addendumStep > 0 ? addendumStep : 1;
  let newConversationId = null;

  setAddendumStatus(AddendumStatus.ACTIVE);
  setAddendumStep(currentStep);

  try {
    await runStep(1, createQuote);
    await runStep(2, downloadAddendum);
    newConversationId = (await runStep(3, submitDealData)) ?? null;
    setAddendumStatus(AddendumStatus.INACTIVE);
  } catch (error) {
    setAddendumStatus(AddendumStatus.FAILED);
  }

  if (quoteIdRef.current) {
    vehicleApi.saveBalloonDealQuote({
      conversation_id: newConversationId ?? dealSourceData.conversation_id,
      original_conversation_id: dealSourceData.conversation_id,
      dealer_id: afgDealerId,
      quote_id: quoteIdRef.current,
    });
  }
};
